import React, { useState } from 'react';
import { X, Download, FileSpreadsheet, ChevronDown } from 'lucide-react';
import userService from '../../../../services/userService';

export default function ExportRiskFilterModal({ 
  isOpen, 
  onClose, 
  onExport, 
  exportType, 
  loading, 
  filters, 
  onFilterChange,
  authTokens 
}) {
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    onFilterChange('assigned_user', query);

    if (query.length >= 2) {
      setUserSearchLoading(true);
      try {
        const result = await userService.filterUsersBySearch(query, authTokens.access);
        if (result.success) {
          setUserSearchResults(result.data || []);
          setShowUserDropdown(true);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setUserSearchLoading(false);
      }
    } else {
      setUserSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const selectUser = (user) => {
    onFilterChange('assigned_user', user.email);
    setShowUserDropdown(false);
    setUserSearchResults([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Export {exportType === 'pdf' ? 'PDF' : 'Excel'} - Risques
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          {/* Risk Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom du risque</label>
            <input
              type="text"
              value={filters.risk_name}
              onChange={(e) => onFilterChange('risk_name', e.target.value)}
              placeholder="Rechercher par nom..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Assigned User with Searchable Dropdown */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Utilisateur assigné</label>
            <div className="relative">
              <input
                type="text"
                value={filters.assigned_user}
                onChange={handleUserSearch}
                placeholder="Rechercher un utilisateur..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            
            {showUserDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                {userSearchLoading ? (
                  <div className="px-3 py-2 text-sm text-gray-500">Recherche...</div>
                ) : userSearchResults.length > 0 ? (
                  userSearchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => selectUser(user)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      <div className="font-medium">{user.first_name} {user.last_name}</div>
                      <div className="text-gray-500 text-xs">{user.email}</div>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">Aucun utilisateur trouvé</div>
                )}
              </div>
            )}
          </div>

          {/* Probability - Risk specific values */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Probabilité</label>
            <select
              value={filters.probability}
              onChange={(e) => onFilterChange('probability', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Toutes les probabilités</option>
              <option value="mineur">Mineur</option>
              <option value="normal">Normal</option>
              <option value="majeur">Majeur</option>
              <option value="critique">Critique</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="approved">Approuvé</option>
              <option value="rejected">Rejeté</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Créé à partir du</label>
              <input
                type="date"
                value={filters.created_date_from}
                onChange={(e) => onFilterChange('created_date_from', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Créé jusqu'au</label>
              <input
                type="date"
                value={filters.created_date_to}
                onChange={(e) => onFilterChange('created_date_to', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Ordering */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tri</label>
            <select
              value={filters.ordering}
              onChange={(e) => onFilterChange('ordering', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="newest">Plus récents</option>
              <option value="oldest">Plus anciens</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onExport}
            disabled={loading}
            className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 flex items-center ${
              exportType === 'pdf' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
            {exportType === 'pdf' ? (
              <><Download className="h-4 w-4 mr-2" />Exporter PDF</>
            ) : (
              <><FileSpreadsheet className="h-4 w-4 mr-2" />Exporter Excel</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}